import { Form } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import {
  formatTaxNumber,
  validateTaxNumber
} from "../../../../../utils/validation/tax-number.ts"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { type TeamMembersFilterOptions } from "../../../../TeamManagementPage/redux/team-members.types.ts"
import { type SettingsCompanyFormData } from "../SettingsCompanyForm.tsx"
import { CompanyInformationFormAddress } from "./CompanyInformationFormAddress.tsx"
import { CompanyAlertThresholdsFormSection } from "./CompanyAlertThresholdsFormSection.tsx"
import { CompanyInvoiceDefaultsFormSection } from "./CompanyInvoiceDefaultsFormSection.tsx"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface CompanyInformationFormSectionProps {
  options: TeamMembersFilterOptions | null
  form: FormInstance<SettingsCompanyFormData>
}

export function CompanyInformationFormSection({
  options,
  form
}: CompanyInformationFormSectionProps) {
  return (
    <>
      <div data-guide-target="company-details-section">
        <Form.Item<SettingsCompanyFormData>
          label={"Company Name"}
          name="business_name"
          rules={[{ required: true, message: "Please enter your company name" }]}
        >
          <TextInput placeholder="Enter company name" />
        </Form.Item>
      </div>

      <Form.Item<SettingsCompanyFormData>
        label={"Tax ID / VAT"}
        name="ein"
        rules={[{ validator: validateTaxNumber }]}
      >
        <TextInput
          disableAutoCapitalize
          placeholder={placeholders.taxNumber}
          maxLength={10}
          onChange={(e) => {
            form.setFieldValue("ein", formatTaxNumber(e.target.value))
          }}
          onPaste={(e) => {
            e.preventDefault()
            const pastedText = e.clipboardData.getData("text")
            form.setFieldValue("ein", formatTaxNumber(pastedText))
          }}
        />
      </Form.Item>

      <CompanyInformationFormAddress options={options} />

      <Form.Item<SettingsCompanyFormData>
        label={"Phone"}
        name="company_phone"
        rules={[{ required: true, validator: validatePhone }]}
      >
        <PhoneInput placeholder={placeholders.phone} />
      </Form.Item>

      {/* License and Insurance Information */}
      <div data-guide-target="company-license-section">
        <Form.Item<SettingsCompanyFormData>
          label={"License Number"}
          name="license_number"
          rules={[
            { required: true, message: "Please enter your license number" },
            {
              pattern: /^\d{1,7}$/,
              message: "License number must be up to 7 digits"
            }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            placeholder="Enter license number"
            maxLength={7}
          />
        </Form.Item>
      </div>

      <Form.Item<SettingsCompanyFormData>
        label={"Bond Number"}
        name="bond_number"
      >
        <TextInput disableAutoCapitalize placeholder="Enter bond number" />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"General Liability"}
        name="general_liability"
      >
        <TextInput disableAutoCapitalize placeholder="Enter general liability number" />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"Insurance Number"}
        name="insurance_number"
      >
        <TextInput disableAutoCapitalize placeholder="Enter insurance number" />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"Lead Cost Method"}
        name="lead_cost_method"
      >
        <ResolvedSelect
          options={options?.["lead_cost_method"]}
          placeholder="Enter lead cost method"
        />
      </Form.Item>

      <Form.Item<SettingsCompanyFormData>
        label={"Company Bio"}
        name="company_description"
      >
        <TextArea
          rows={4}
          placeholder="Enter a description of your company, services, and expertise..."
        />
      </Form.Item>

      <CompanyInvoiceDefaultsFormSection />

      <CompanyAlertThresholdsFormSection />

      {/* Payment Information */}
      <Form.Item<SettingsCompanyFormData>
        label={"Account for Receiving Money"}
        name="receiving_account"
      >
        <TextInput
          disableAutoCapitalize
          placeholder="Enter receiving account"
        />
      </Form.Item>
    </>
  )
}
